import React, { createContext } from "react";
import { jwtDecode } from "jwt-decode";
import PropTypes from "prop-types";

const emptySession = {
	user: null,
	userToken: null,
	impersonatedBy: null,
	impersonatedByToken: null,
};

export const UserContext = createContext( {
	userSession: emptySession,
} );

/**
 * Map a JWT access token to a user session object.
 *
 * @param {string} accessToken The encoded access token of the logged in user.
 *
 * @returns {Object} the userSession.
 */
const getSessionForAccessToken = ( accessToken ) => {
	let decodedToken;
	try {
		decodedToken = jwtDecode( accessToken );
	} catch ( e ) {
		return emptySession;
	}

	const impersonatorToken = decodedToken.impersonatorToken;
	let decodedImpersonatorToken = null;
	if ( impersonatorToken ) {
		try {
			decodedImpersonatorToken = jwtDecode( impersonatorToken );
		} catch ( e ) {
			// Ignore decode errors for impersonator token
		}
	}

	return {
		user: decodedToken,
		userToken: accessToken,
		impersonatedBy: decodedImpersonatorToken,
		impersonatedByToken: impersonatorToken || null,
	};
};

/**
 * A wrapper of the UserContext.Provider with the added benefit of it transforming a JWT access token into a userSession.
 *
 * @param {Object} props The props.
 *
 * @returns {JSX.Element} The provider
 */
export const UserContextProvider = ( props ) => {
	const userSession = getSessionForAccessToken( props.accessToken );
	return (
		<UserContext.Provider value={ { userSession } }>
			{ props.children }
		</UserContext.Provider>
	);
};

UserContextProvider.propTypes    = {
	accessToken: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
};
